import { useCallback, useEffect, useState } from 'react'

export const useViewRefresh = (
  viewScopeId: string,
  observedElIds: Array<string>
) => {
  const [observedElMap, setObservedElMap] = useState<{
    [id: string]: HTMLElement | null
  }>({})

  const observedElIdsKey = observedElIds.join(',')

  useEffect(() => {
    const elMap =
      observedElIds
        ?.map((obsId) => {
          const el = document.getElementById(obsId)
          return el ? { [obsId]: el } : null
        })
        .filter(Boolean)
        .reduce<Record<string, HTMLElement>>(
          (accumulator, currentValue) =>
            Object.assign(accumulator, currentValue),
          {}
        ) || {}
    setObservedElMap(elMap)
  }, [observedElIdsKey])

  const updateObservedElMap = useCallback(() => {
    setObservedElMap((prev) => {
      const currentElMap: { [id: string]: HTMLElement | null } = {}
      Object.keys(prev).forEach((elId: string) => {
        currentElMap[elId] = document.getElementById(elId)
      })
      const hasChanged = Object.keys(currentElMap).some(
        (elId: string) => currentElMap[elId] !== prev[elId]
      )
      return hasChanged ? { ...currentElMap } : prev
    })
  }, [])

  const handleMutations = useCallback(
    (mutations: MutationRecord[]) => {
      const hasChildListMutation = mutations?.some(
        (mutation) => mutation.type === 'childList'
      )
      if (hasChildListMutation) {
        updateObservedElMap()
      }
    },
    [updateObservedElMap]
  )

  useEffect(() => {
    const scopeEl = document.getElementById(viewScopeId)
    if (!scopeEl) {
      return undefined
    }
    const mutationObserver = new MutationObserver(handleMutations)
    mutationObserver.observe(scopeEl, {
      childList: true,
      subtree: true
    })
    return () => {
      mutationObserver.disconnect()
    }
  }, [viewScopeId, handleMutations])

  return observedElMap
}
